import React, { useState, useEffect, useRef, useMemo } from 'react';

const AdvancedFilterPanel = ({
  teachers = [],
  students = [],
  lessonGroups = [],
  onFilterChange,
  totalTeachers = 0,
  totalStudents = 0
}) => {
  const [teacherFilter, setTeacherFilter] = useState('');
  const [studentFilter, setStudentFilter] = useState('');
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [showTeacherDropdown, setShowTeacherDropdown] = useState(false);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);

  const teacherDropdownRef = useRef(null);
  const studentDropdownRef = useRef(null);

  const stats = useMemo(() => ({
    activeTeachers: teachers.filter(t => t.status === 1 || t.status === '1').length,
    pendingTeachers: teachers.filter(t => t.status === 2 || t.status === '2').length,
    activeStudents: students.filter(s => s.status === 1 || s.status === '1').length,
    passiveStudents: students.filter(s => s.status === 0 || s.status === '0').length,
  }), [teachers, students]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (teacherDropdownRef.current && !teacherDropdownRef.current.contains(event.target)) {
        setShowTeacherDropdown(false);
      }
      if (studentDropdownRef.current && !studentDropdownRef.current.contains(event.target)) {
        setShowStudentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredTeachers = useMemo(() => {
    if (!teacherFilter) return [];
    const searchTerm = teacherFilter.toLowerCase();
    return teachers.filter(teacher => {
      const matchesSearch =
        (teacher.adi && teacher.adi.toLowerCase().includes(searchTerm)) ||
        (teacher.e_posta && teacher.e_posta.toLowerCase().includes(searchTerm)) ||
        (teacher.telefon && teacher.telefon.includes(searchTerm));
      const notSelected = !selectedTeachers.find(t => t.id === teacher.id);
      return matchesSearch && notSelected;
    }).slice(0, 10);
  }, [teacherFilter, teachers, selectedTeachers]);

  const filteredStudents = useMemo(() => {
    if (!studentFilter) return [];
    const searchTerm = studentFilter.toLowerCase();
    return students.filter(student => {
      const matchesSearch =
        (student.name && student.name.toLowerCase().includes(searchTerm)) ||
        (student.phone && student.phone.includes(searchTerm)) ||
        (student.parent_phone && student.parent_phone.includes(searchTerm)) ||
        (student.email && student.email.toLowerCase().includes(searchTerm));
      const notSelected = !selectedStudents.find(s => s.id === student.id);
      return matchesSearch && notSelected;
    }).slice(0, 10);
  }, [studentFilter, students, selectedStudents]);

  const handleTeacherSelect = (teacher) => {
    const newSelected = [...selectedTeachers, teacher];
    setSelectedTeachers(newSelected);
    setTeacherFilter('');
    setShowTeacherDropdown(false);
    onFilterChange?.({ selectedTeachers: newSelected, selectedStudents });
  };

  const handleStudentSelect = (student) => {
    const newSelected = [...selectedStudents, student];
    setSelectedStudents(newSelected);
    setStudentFilter('');
    setShowStudentDropdown(false);
    onFilterChange?.({ selectedTeachers, selectedStudents: newSelected });
  };

  const removeTeacher = (teacherId) => {
    const newSelected = selectedTeachers.filter(t => t.id !== teacherId);
    setSelectedTeachers(newSelected);
    onFilterChange?.({ selectedTeachers: newSelected, selectedStudents });
  };

  const removeStudent = (studentId) => {
    const newSelected = selectedStudents.filter(s => s.id !== studentId);
    setSelectedStudents(newSelected);
    onFilterChange?.({ selectedTeachers, selectedStudents: newSelected });
  };

  const clearAllFilters = () => {
    setSelectedTeachers([]);
    setSelectedStudents([]);
    setTeacherFilter('');
    setStudentFilter('');
    onFilterChange?.({ selectedTeachers: [], selectedStudents: [] });
  };

  const getStatusDot = (status) => {
    if (status === 1 || status === '1') return 'bg-emerald-500';
    if (status === 2 || status === '2') return 'bg-amber-400';
    return 'bg-red-400';
  };

  const getStatusText = (status) => {
    if (status === 1 || status === '1') return 'Aktif';
    if (status === 2 || status === '2') return 'Beklemede';
    return 'Pasif';
  };

  const Stat = ({ label, value }) => (
    <div className="px-3 py-2 rounded-md border border-gray-200 bg-white">
      <div className="text-lg font-semibold text-gray-900 leading-tight">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );

  const inputClass = "w-full pl-8 pr-3 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 transition-colors";

  return (
    <div className="space-y-3">
      {/* İstatistikler */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <Stat label="Öğretmen" value={totalTeachers} />
        <Stat label="Aktif öğretmen" value={stats.activeTeachers} />
        <Stat label="Beklemede" value={stats.pendingTeachers} />
        <Stat label="Öğrenci" value={totalStudents} />
        <Stat label="Aktif öğrenci" value={stats.activeStudents} />
        <Stat label="Pasif öğrenci" value={stats.passiveStudents} />
      </div>

      {/* Filtre alanları */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div ref={teacherDropdownRef} className="relative">
          <label className="block text-xs font-medium text-gray-600 mb-1">Öğretmen</label>
          <div className="relative">
            <svg className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              className={inputClass}
              placeholder="İsim, e-posta veya telefon…"
              value={teacherFilter}
              onChange={(e) => setTeacherFilter(e.target.value)}
              onFocus={() => setShowTeacherDropdown(true)}
            />
            {showTeacherDropdown && filteredTeachers.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white rounded-md border border-gray-200 shadow-md max-h-60 overflow-y-auto">
                {filteredTeachers.map(teacher => (
                  <button
                    type="button"
                    key={teacher.id}
                    className="w-full px-3 py-2 hover:bg-gray-50 text-left flex items-center justify-between gap-2"
                    onClick={() => handleTeacherSelect(teacher)}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{teacher.adi}</div>
                      <div className="text-xs text-gray-500 truncate">{teacher.e_posta}</div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap">
                      <span className={`w-1.5 h-1.5 rounded-full ${getStatusDot(teacher.status)}`} />
                      {getStatusText(teacher.status)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedTeachers.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {selectedTeachers.map(teacher => (
                <span
                  key={teacher.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
                >
                  {teacher.adi}
                  <button
                    onClick={() => removeTeacher(teacher.id)}
                    className="text-gray-400 hover:text-gray-900"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div ref={studentDropdownRef} className="relative">
          <label className="block text-xs font-medium text-gray-600 mb-1">Öğrenci</label>
          <div className="relative">
            <svg className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              className={inputClass}
              placeholder="İsim, e-posta veya telefon…"
              value={studentFilter}
              onChange={(e) => setStudentFilter(e.target.value)}
              onFocus={() => setShowStudentDropdown(true)}
            />
            {showStudentDropdown && filteredStudents.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white rounded-md border border-gray-200 shadow-md max-h-60 overflow-y-auto">
                {filteredStudents.map(student => (
                  <button
                    type="button"
                    key={student.id}
                    className="w-full px-3 py-2 hover:bg-gray-50 text-left flex items-center justify-between gap-2"
                    onClick={() => handleStudentSelect(student)}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{student.name}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {student.phone} {student.parent_phone && `/ ${student.parent_phone}`}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap">
                      <span className={`w-1.5 h-1.5 rounded-full ${getStatusDot(student.status)}`} />
                      {getStatusText(student.status)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedStudents.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {selectedStudents.map(student => (
                <span
                  key={student.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
                >
                  {student.name}
                  <button
                    onClick={() => removeStudent(student.id)}
                    className="text-gray-400 hover:text-gray-900"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {(selectedTeachers.length > 0 || selectedStudents.length > 0) && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            Aktif:
            {selectedTeachers.length > 0 && <span className="ml-1">{selectedTeachers.length} öğretmen</span>}
            {selectedStudents.length > 0 && <span className="ml-1">{selectedStudents.length} öğrenci</span>}
          </span>
          <button
            onClick={clearAllFilters}
            className="text-gray-500 hover:text-gray-900"
          >
            Temizle
          </button>
        </div>
      )}
    </div>
  );
};

export default AdvancedFilterPanel;
